<template>
  <div class="container max-width-container">
    <section class="section">
      <div class="columns is-multiline is-centered">
        <div class="column is-paddingless">
          <div class="jobs-overview">
            <h2 :class="{'heading-2': $mq.desktop, 'heading-3': !$mq.desktop}">
              {{ titleText }}
            </h2>
            <div class="columns is-desktop">
              <div class="column is-4-desktop left-column">
                <div :id="filtersId" class="job-filter-illustration">
                  <jobs-filter-illustration></jobs-filter-illustration>
                </div>
                <div :class="{sticky: $mq.widescreen}">
                  <search-field :initial-value="routeQuerySearch" @submit="onSearchFieldSubmit"></search-field>
                  <filter-container @after-update-filters="getOptionsAndJobsByActiveFilters"></filter-container>
                  <div v-if="$mq.widescreen">
                    <p class="jobs-short-title paragraph-m" v-html="shortTitleText"></p>
                    <jobs-panels-for-register v-if="!isEmployerUser"></jobs-panels-for-register>
                  </div>
                </div>
              </div>
              <div class="column is-8-desktop right-column">
                <jobs-loader v-if="jobsLoadingState"></jobs-loader>
                <div v-else>
                  <p v-if="!$mq.widescreen" :id="jobsListTitleId" class="jobs-short-title paragraph-m" v-html="shortTitleText"></p>
                  <jobs-list
                    :list="jobsListState"
                    :total-list-count="jobsCountState"
                    :loading="jobsPartialListLoadingState"
                    @load-more-click="handleLoadMoreJobs">
                  </jobs-list>
                  <jobs-panels-for-register v-if="!$mq.widescreen && !isEmployerUser"></jobs-panels-for-register>
                </div>
              </div>
            </div>
            <div>
              <jobs-scroll-buttons-container
                :show="showScrollBtn"
                :jobs-list-title-id="jobsListTitleId"
                :filters-id="filtersId">
              </jobs-scroll-buttons-container>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import 'top20common/distribution/icons/arrow-down';

import {identity, isEmpty, pickBy} from 'lodash';
import JobsFilterIllustration from 'top20common/components/illustrations/JobsFilterIllustration';
import {mapGetters} from 'vuex';

import SearchField from '@/pages/jobs/filters/SearchField';
import utils from '@/utils/utils';

import JobsList from './JobsList';
import JobsLoader from './JobsLoader';
import JobsPanelsForRegister from './JobsPanelsForRegister';
import JobsScrollButtonsContainer from './JobsScrollButtonsContainer';
import FilterContainer from './filters/FiltersContainer';

export default {
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (!from.name || from.name === to.name) {
        vm.$store.commit('mutatePreviousRouteNameState', null);
        vm.$store.commit('mutatePreviousRoutePathState', null);
        vm.$store.commit('mutatePreviousAnchorState', null);
      }
    });
  },
  created() {
    this.$mam.trackJobsOverviewPageVisit(this.$route.query);
    if (this.previousAnchorState && this.jobsListState.length) {
      this.filtersApplied = !isEmpty(this.jobsActiveFiltersState);
      return;
    }
    this.getOptionsAndJobsByActiveFilters();
  },
  data() {
    return {
      filtersId: 'filters',
      jobsListTitleId: 'jobs-list-title',
      filtersApplied: false
    };
  },
  computed: {
    ...mapGetters([
      'jobsLoadingState',
      'jobsPartialListLoadingState',
      'jobsListState',
      'jobsPageState',
      'jobsCountState',
      'jobsActiveFiltersState',
      'jobsOptionsLoadingState',
      'previousAnchorState',
      'jobsSearchQueryState',
      'isEmployerUser'
    ]),
    labels() {
      return {
        jobsLoadingText: this.$gettext('Loading jobs…'),
        jobsTitleText: this.$gettextInterpolate(
          this.$ngettext('%{amount} Open job', '%{amount} Open jobs', this.jobsCountState),
          {amount: this.jobsCountState}
        ),
        jobsShortTitleText: this.$gettextInterpolate(
          this.$ngettext(
            '<span class="paragraph-bold">%{amount}</span> Job',
            '<span class="paragraph-bold">%{amount}</span> Jobs',
            this.jobsCountState
          ),
          this.amountOfJobs
        )
      };
    },
    amountOfJobs() {
      return {amount: this.jobsCountState};
    },
    titleText() {
      if (this.jobsLoadingState || this.jobsOptionsLoadingState) {
        return this.labels.jobsLoadingText;
      }
      return this.labels.jobsTitleText;
    },
    showScrollBtn() {
      return Boolean(this.$mq.phone && this.jobsCountState && this.filtersApplied);
    },
    shortTitleText() {
      if (this.jobsLoadingState || this.jobsOptionsLoadingState) {
        return this.labels.jobsLoadingText;
      }
      return this.labels.jobsShortTitleText;
    },
    routeQuerySearch() {
      return this.$route.query.search;
    }
  },
  methods: {
    getOptionsAndJobsByActiveFilters() {
      this.filtersApplied = false;
      let {utm_source, utm_medium, utm_campaign, utm_content, ...filters} = this.$route.query;
      this.$store.dispatch('getJobsOptionsAction', filters).then(() => {
        let queryParams = utils.getQueryParamsByFiltersState(this.jobsActiveFiltersState);
        let query = {
          utm_source,
          utm_medium,
          utm_campaign,
          utm_content,
          search: this.jobsSearchQueryState,
          ...queryParams
        };
        this.$router.push({query: pickBy(query, identity)});
        this.$store.dispatch('getJobsListAction', this.$route.query).then(() => {
          this.filtersApplied = !isEmpty(queryParams);
        });
      });
    },
    handleLoadMoreJobs() {
      this.$mam.trackLoadMoreJobsClicked();
      this.$store.commit('incrementJobsPageState');
      this.$store.dispatch('getJobsListInBackgroundAction', {
        ...this.$route.query,
        page: this.jobsPageState,
        loadMore: true
      });
    },
    onSearchFieldSubmit(value) {
      this.$store.commit('setJobsSearchQueryState', value);
      this.$router.push({query: {...this.$route.query, search: value}});
      this.getOptionsAndJobsByActiveFilters();
    }
  },
  components: {
    SearchField,
    JobsLoader,
    JobsList,
    JobsPanelsForRegister,
    JobsScrollButtonsContainer,
    JobsFilterIllustration,
    FilterContainer
  }
};
</script>

<style lang="scss" scoped>
@import '~VariablesStyles';

.jobs-overview {
  .sticky {
    position: sticky;
    top: 0;
  }

  .job-filter-illustration {
    svg {
      width: 100%;
      margin: 30px auto;
    }
  }
}

@media (min-width: $tablet) {
  .jobs-overview {
    .heading-2 {
      padding-left: 12px;
    }

    .jobs-short-title {
      padding: 30px 0 20px;
    }
  }
}

@media (max-width: $widescreen) {
  .jobs-overview {
    .left-column,
    .right-column {
      padding: 0;
    }

    .jobs-short-title {
      padding: 40px 0 10px;
    }
  }
}
</style>
